import classNames from 'classnames';
import { Fragment, PropsWithChildren, useCallback, useRef } from 'react';
import { HiX } from 'react-icons/hi';

import { Dialog, Transition } from '@headlessui/react';

import IconButton from '../icon-button/icon-button';

export interface ModalProps {
  className?: string;
  title?: React.ReactNode;
  titleVariant?: 'primary' | 'secondary';
  open?: boolean;
  appear?: boolean;
  onClose?: (value?: boolean) => void;
  onBackdropClick?: () => void;
  closeDisabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  inactive?: boolean;
}

/**
 * Fully-managed renderless dialog component with accessibility and keyboard features provided by headlessui
 */
export function Modal({
  className,
  open = false,
  appear = false,
  titleVariant = 'primary',
  size = 'md',
  onClose,
  onBackdropClick,
  closeDisabled = false,
  title,
  children,
  inactive = false,
}: PropsWithChildren<ModalProps>) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(
    (value?: boolean) => {
      if (inactive) return;
      if (closeDisabled) return;
      if (typeof onClose !== 'function') return;
      onClose(value);
    },
    [closeDisabled, inactive, onClose],
  );

  const handleBackdropClick = useCallback(() => {
    if (inactive) return;
    if (closeDisabled) return;
    if (typeof onBackdropClick !== 'function') return;
    onBackdropClick();
  }, [closeDisabled, inactive, onBackdropClick]);

  return (
    <Transition.Root show={inactive ? false : open} appear={appear} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40"
        onClose={handleBackdropClick}
        static={true}
        open={inactive ? false : undefined}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  size === 'sm' ? 'w-full sm:max-w-md sm:w-full' : undefined,
                  size === 'md' ? 'w-full sm:max-w-lg sm:w-full' : undefined,
                  size === 'lg' ? 'w-full sm:max-w-2xl sm:w-full' : undefined,
                  size === 'xl' ? 'w-full sm:max-w-3xl sm:w-full' : undefined,
                  size === '2xl' ? 'w-full sm:max-w-4xl sm:w-full' : undefined,
                  size === '3xl' ? 'w-full sm:max-w-6xl sm:w-full' : undefined,
                )}
              >
                <div
                  className={classNames(
                    'relative bg-surface-base dark:bg-gray-900 rounded-lg text-left text-white dark:text-white overflow-hidden shadow-xl transform transition-all sm:my-8',
                    'flex flex-1 flex-col',
                    className,
                  )}
                >
                  <div className={classNames('text-left', 'flex flex-1 flex-col')}>
                    {title && (
                      <Dialog.Title
                        as="h3"
                        className={classNames(
                          titleVariant === 'primary'
                            ? 'flex items-center justify-between text-xl px-4 py-2 leading-6 font-medium text-brand-primary dark:text-white'
                            : undefined,
                          titleVariant === 'secondary'
                            ? 'flex items-center justify-between px-8 py-2 leading-6 tracking-normal font-normal text-secondary dark:text-white'
                            : undefined,
                        )}
                      >
                        {title}
                        {typeof onClose === 'function' && (
                          <div className="z-[1]">
                            <IconButton
                              className="-mr-2"
                              ref={closeButtonRef}
                              onClick={() => handleClose(!open)}
                              disabled={closeDisabled}
                            >
                              <span className="sr-only">Close</span>
                              <HiX />
                            </IconButton>
                          </div>
                        )}
                      </Dialog.Title>
                    )}

                    <div className={classNames('', 'flex flex-col flex-1 items-stretch')}>{children}</div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default Modal;
